import { NgClass } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChildren,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelectCheckboxComponent } from './multi-select-checkbox/multi-select-checkbox.component';
import { MultipleSelectGroupDirective } from './multiple-select-group.directive';

@Component({
  selector: 'app-multi-select',
  imports: [NgbDropdownModule, NgClass, TranslateModule],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectComponent
  implements ControlValueAccessor, AfterContentInit
{
  private cdr = inject(ChangeDetectorRef);

  public footer = input<boolean>(false);
  protected placeholder = input<string>('shared.select.placeholder');
  // header currently works only for non grouped select
  protected header = input<boolean>(false);
  protected selected = input<any[]>([]);
  protected changed = output<any[]>();
  protected size = input<string>('md');
  protected status = input<string>(null);

  public value = signal<any[]>([]);
  protected searchValue = signal<string>('');
  protected disabled = signal<boolean>(false);
  protected label = signal<string>('');
  protected buttonWidth = signal<number>(0);

  private checkboxes = contentChildren<MultiSelectCheckboxComponent>(
    forwardRef(() => MultiSelectCheckboxComponent),
    {
      descendants: true,
    }
  );
  private groups = contentChildren<MultipleSelectGroupDirective>(
    forwardRef(() => MultipleSelectGroupDirective),
    {
      descendants: true,
    }
  );
  private dropdownToggle = viewChild<ElementRef>('dropdownToggle');
  private dropdownMenu = viewChild<ElementRef>('dropdownMenu');
  private dropdown = viewChild<NgbDropdown>('dropdown');

  private onChange: any = () => {};
  private onTouched: any = () => {};

  private ngControl = inject(NgControl, { self: true, optional: true });

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      if (!this.ngControl) {
        this.value.set(this.selected());
      }
    });
  }

  ngAfterContentInit(): void {
    if (this.ngControl) {
      this.apply(false);
    } else if (this.selected()) {
      this.value.set(this.selected());
      this.apply(false);
    }
  }

  writeValue(obj: any): void {
    this.value.set(obj);
    this.apply(false);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public apply(toggleChange = true) {
    const selectedCheckboxes = this.checkboxes().filter(
      (checkbox) =>
        checkbox.isChecked() && !this.isGroupParentCheckbox(checkbox)
    );

    if (this.footer()) {
      this.value.set(
        selectedCheckboxes
          .map((checkbox) => checkbox.value())
          .filter((value) => value)
      );
    }

    if (toggleChange) {
      this.onChange(this.value());
      this.changed.emit(this.value());
    }

    this.label.set(
      selectedCheckboxes.map((checkbox) => checkbox.label()).join(', ')
    );

    if (this.footer()) {
      this.dropdown()?.close();
    }

    this.cdr.markForCheck();
  }

  protected clear() {
    this.value.set([]);
    this.onChange(this.value());

    this.changed.emit(this.value());
    this.checkboxes().forEach((checkbox) => checkbox.isChecked.set(false));

    this.label.set('');

    this.dropdown()?.close();
  }

  protected openChange(isOpened: boolean) {
    if (isOpened) {
      this.checkboxes().forEach((checkbox) => checkbox.check());
      this.groups().forEach((group) => group.updateParentChecked());
      this.cdr.markForCheck();
      requestAnimationFrame(() => this.setMenuWidth());

      return;
    }

    if (!isOpened) {
      this.search('');

      if (this.footer()) {
        this.checkboxes()
          .filter(
            (checkbox) =>
              checkbox.isVisible() && !this.isGroupParentCheckbox(checkbox)
          )
          .forEach((checkbox) => checkbox.check());
      }
    }
  }

  protected onToggle() {
    this.buttonWidth.set(this.dropdownToggle().nativeElement.offsetWidth);
  }

  private setMenuWidth() {
    const menu = this.dropdownMenu()?.nativeElement;

    if (!menu) {
      return;
    }

    this.buttonWidth.set(menu.getBoundingClientRect().width);
  }

  protected search(searchValue: string) {
    this.searchValue.set(searchValue);
    const normalizedSearchValue = searchValue.toLowerCase();

    if (!normalizedSearchValue) {
      this.groups().forEach((group) => group.search(''));

      this.checkboxes().forEach((checkbox) => {
        checkbox.isVisible.set(true);
      });

      return;
    }

    this.groups().forEach((group) => group.search(normalizedSearchValue));

    this.checkboxes().forEach((checkbox) => {
      const isGrouped = this.groups().some((group) =>
        group.checkboxes().includes(checkbox)
      );

      if (!isGrouped) {
        checkbox.isVisible.set(
          checkbox.isChecked() ||
            checkbox.label()?.toLowerCase().includes(normalizedSearchValue)
        );
      }
    });
  }

  public refreshSearch() {
    if (this.searchValue()) {
      this.search(this.searchValue());
    }
  }

  public updateGroupParent(checkbox: MultiSelectCheckboxComponent) {
    const group = this.groups().find(
      (group) => group.checkboxes()[0] === checkbox
    );
    group?.setChildrenChecked(checkbox.isChecked());
  }

  public isGroupParentCheckbox(checkbox: MultiSelectCheckboxComponent) {
    return this.groups().some((group) => group.checkboxes()[0] === checkbox);
  }

  protected isOpen() {
    return this.dropdown()?.isOpen();
  }
}
